import React from 'react';
import matching from '../../assets/square-transparent-matching.png';
import companyIcon from '../../assets/icons/company.png';
import industryIcon from '../../assets/icons/industry.png';
import foundersIcon from '../../assets/icons/founders.png';
import ownershipIcon from '../../assets/icons/ownership.png';
import supportIcon from '../../assets/icons/support.png';
import checkmarkCharcoal from '../../assets/checkmark_lt_LIGHT-charcoal.png';

interface BreadcrumbStep {
  id: string;
  title: string;
  description: string;
}

interface ProfileBreadcrumbsProps {
  steps: BreadcrumbStep[];
  currentStep: number;
  completedSteps: number[];
  onStepClick: (stepIndex: number) => void;
  userType?: 'startup' | 'investor';
  embedded?: boolean;
  /** Profile is live / approved — Matching Starts circle stays green. */
  matchingLive?: boolean;
  /** Steps after this index stay closed until the one before them is saved. */
  maxReachableStep?: number;
}

const ProfileBreadcrumbs: React.FC<ProfileBreadcrumbsProps> = ({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  userType: _userType,
  embedded = false,
  matchingLive = false,
  maxReachableStep,
}) => {
  if (currentStep === steps.length - 1) {
    return null;
  }
  const matchingStepIndex = steps.length - 1;
  const isStepCompleted = (stepIndex: number) =>
    completedSteps.includes(stepIndex) || (matchingLive && stepIndex === matchingStepIndex);
  const isStepActive = (stepIndex: number) => stepIndex === currentStep;
  // Open saved steps plus the next unlocked one (maxReachableStep). Matching Starts is display-only.
  const isStepClickable = (stepIndex: number) => {
    if (stepIndex === matchingStepIndex) return false;
    if (typeof maxReachableStep === 'number') {
      return stepIndex <= maxReachableStep;
    }
    return isStepCompleted(stepIndex) || isStepActive(stepIndex);
  };

  // Map step IDs to their icons
  const getStepIcon = (stepId: string) => {
    const iconMap: Record<string, string> = {
      company: companyIcon,
      'industry-stage': industryIcon,
      'founders-team': foundersIcon,
      'ownership-traction': ownershipIcon,
      'external-support': supportIcon,
      'matching-starts': matching,
    };
    return iconMap[stepId] || companyIcon;
  };

  const getMobileBorderColor = (stepIndex: number) => {
    if (isStepActive(stepIndex)) {
      return 'border-emerald-500';
    } else if (isStepCompleted(stepIndex)) {
      return 'border-emerald-500/70';
    } else {
      return 'border-[#303030]';
    }
  };

  const getStepColor = (stepIndex: number) => {
    if (isStepActive(stepIndex)) {
      return 'bg-emerald-500'; // Bright green for active
    } else if (isStepCompleted(stepIndex)) {
      return 'bg-emerald-500/70'; // Lighter green for completed
    } else {
      return 'bg-[#303030]'; // PRAIRI panel grey for incomplete
    }
  };

  const getTriangleColor = (stepIndex: number) => {
    if (isStepActive(stepIndex)) {
      return 'border-l-emerald-500';
    } else if (isStepCompleted(stepIndex)) {
      return 'border-l-emerald-500/70';
    } else {
      return 'border-l-[#303030]';
    }
  };

  return (
    <div className="mb-4">
      {/* Mobile: Icons only with bottom borders */}
      <nav className="flex md:hidden justify-around items-center w-full px-2">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`pb-2 border-b-2 h-10 flex items-center justify-center relative ${getMobileBorderColor(index)} ${
              isStepClickable(index) ? 'cursor-pointer' : 'cursor-default'
            }`}
            onClick={() => {
              if (isStepClickable(index)) {
                onStepClick(index);
              }
            }}
          >
            <img src={getStepIcon(step.id)} alt={step.title} className="h-full" />
            {isStepCompleted(index) && !isStepActive(index) && (
              <img
                src={checkmarkCharcoal}
                alt=""
                className="absolute bottom-1 right-0 w-3.5 h-3.5 object-contain"
                aria-hidden
              />
            )}
          </div>
        ))}
      </nav>

      {/* Desktop: Text with triangular breadcrumb shapes */}
      <div className={`hidden md:flex items-center justify-center ${embedded ? 'py-2' : 'py-4'}`}>
        <div className="flex items-center">
          {steps.slice(0, -1).map((step, index) => (
            <div
              key={step.id}
              className={`relative flex items-center ${
                isStepActive(index) ? 'text-[#111111]' : 'text-white'
              } ${
                isStepClickable(index) ? 'cursor-pointer hover:opacity-90' : 'cursor-not-allowed'
              }`}
              onClick={() => {
                if (isStepClickable(index)) {
                  onStepClick(index);
                }
              }}
            >
              {/* Main rectangle with triangular cutout on left (for non-first) */}
              <div
                className={`relative flex items-center ${embedded ? 'h-7' : 'h-10'} ${getStepColor(index)} ${
                  index === 0 ? 'rounded-l-full' : ''
                }`}
              >
                {/* Left triangle CUTOUT for non-first breadcrumbs */}
                {index > 0 && (
                  <div
                    className={`absolute left-0 w-0 h-0 border-solid border-y-transparent border-l-dark-950 ${embedded ? 'border-t-[14px] border-b-[14px] border-l-[14px]' : 'border-t-[20px] border-b-[20px] border-l-[20px]'}`}
                  ></div>
                )}

                {/* Text content */}
                <div
                  className={`relative flex items-center justify-center ${
                    isStepActive(index) ? 'font-bold text-[#111111]' : 'font-medium text-white'
                  } ${embedded ? 'text-xs' : 'text-sm'} py-2 ${
                    index === 0
                      ? embedded
                        ? 'pl-4 pr-2'
                        : 'pl-6 pr-3'
                      : embedded
                        ? 'pl-5 pr-2'
                        : 'pl-8 pr-3'
                  } transition-all duration-200 whitespace-nowrap`}
                >
                  {step.title}
                </div>
              </div>

              {/* Right triangle arrow */}
              <div
                className={`w-0 h-0 border-solid border-y-transparent ${getTriangleColor(index)} ${embedded ? 'border-t-[14px] border-b-[14px] border-l-[14px]' : 'border-t-[20px] border-b-[20px] border-l-[20px]'}`}
              ></div>
            </div>
          ))}

          {/* Matching Starts: green when completed — not a link */}
          <div
            className={`${embedded ? 'ml-2 w-8 h-8' : 'ml-3 w-12 h-12'} flex-shrink-0 flex cursor-default items-center justify-center overflow-hidden rounded-full transition-all duration-200 ${getStepColor(
              steps.length - 1
            )} ${isStepCompleted(steps.length - 1) || isStepActive(steps.length - 1) ? '' : 'opacity-50'}`}
            aria-hidden
          >
            <img
              src={matching}
              alt="Matching Starts"
              className={`${embedded ? 'w-8 h-8' : 'w-12 h-12'} object-contain`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileBreadcrumbs;
